import type {
  CustomDomain,
  CustomDomainMigrationAction,
  CustomDomainMigrationType,
  CustomDomainType,
} from "@dashboardApi/apis/companiesCompanyIdCustomDomainsDomainTypeDomainId";

import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import sharedConstants, { type SHARED_CONSTANTS_CUSTOM_DOMAIN_MIGRATION_ERRORS } from "@lib/shared_constants";

export type { CustomDomain, CustomDomainMigrationAction, CustomDomainMigrationType };

export interface PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationProps {
  companyId: string;
  domainType: CustomDomainType;
  domainId: string;
  /**
   * `start` from no in-progress row; `advance` to the next validated step;
   * `finish` only on restore; `cancel` before DNS records are converted.
   */
  action: CustomDomainMigrationAction;
  migrationType: CustomDomainMigrationType;
}

/** Same Domain DTO as GET detail, after the requested action. */
export type PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationResult = CustomDomain;

export type PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationAlreadyInProgressError =
  FetchUserEncodedError<SHARED_CONSTANTS_CUSTOM_DOMAIN_MIGRATION_ERRORS["ALREADY_IN_PROGRESS"]>;

export type PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationInvalidStepError = FetchUserEncodedError<
  SHARED_CONSTANTS_CUSTOM_DOMAIN_MIGRATION_ERRORS["INVALID_STEP"]
>;

export type PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationNotInProgressError =
  FetchUserEncodedError<SHARED_CONSTANTS_CUSTOM_DOMAIN_MIGRATION_ERRORS["NOT_IN_PROGRESS"]>;

export type PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationNotMigratableError =
  FetchUserEncodedError<SHARED_CONSTANTS_CUSTOM_DOMAIN_MIGRATION_ERRORS["NOT_MIGRATABLE"]>;

/** Primary-read snapshot returned when advance is rejected because groups still use the domain. */
export interface CustomDomainMigrationRemainingSubscriptionGroupsMeta {
  remaining_count: number;
  remaining_subscription_group_ids: string[];
}

export type PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationRemainingSubscriptionGroupsError =
  FetchUserEncodedError<
    SHARED_CONSTANTS_CUSTOM_DOMAIN_MIGRATION_ERRORS["REMAINING_SUBSCRIPTION_GROUPS"],
    CustomDomainMigrationRemainingSubscriptionGroupsMeta
  >;

export type PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationTtlTooHighError = FetchUserEncodedError<
  SHARED_CONSTANTS_CUSTOM_DOMAIN_MIGRATION_ERRORS["TTL_TOO_HIGH"],
  { dns_ttl: number }
>;

/** Union of encoded errors returned by POST .../migration. FF-off is a 403, not one of these. */
export type PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationResultErrors =
  | PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationAlreadyInProgressError
  | PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationInvalidStepError
  | PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationNotInProgressError
  | PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationNotMigratableError
  | PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationRemainingSubscriptionGroupsError
  | PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationTtlTooHighError;

export const isPostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationError = (
  error: FetchRequestError
): error is PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationResultErrors => {
  return error instanceof FetchUserEncodedError;
};

export const isRemainingSubscriptionGroupsMigrationError = (
  error: FetchRequestError
): error is PostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationRemainingSubscriptionGroupsError => {
  return (
    isPostCompaniesCompanyIdCustomDomainsDomainTypeDomainIdMigrationError(error) &&
    error.errorCode === sharedConstants.CUSTOM_DOMAIN_MIGRATION.ERRORS.REMAINING_SUBSCRIPTION_GROUPS
  );
};
